import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { ProgressBar } from 'primereact/progressbar'
import { FaChartPie, FaPrint, FaRegIdCard } from 'react-icons/fa'
import { Badge, Btn, ConfirmDialog, CustomPanel, PageHeader } from '../../../../components'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import * as colors from '../../../../assets/js/colors'
import {
  parsePreenchimentoStatus,
  ResultadoResposta,
  ROTAS_CHECKLIST,
} from '../../../../constants/checklist-operacional'
import { urlReportPreenchimentoResultado } from '../../../../services/api/routes/checklist-operacional.routes'
import { openReportInNewTab } from '../../../../services/util/report.service'
import ChecklistForm from '../shared/ChecklistForm/ChecklistForm'
import { contarRespondidos, formatarDataHora, formatarPercentual } from '../shared/respostas.util'
import usePreencherChecklist, { LOAD_MESSAGE, MODOS } from './PreencherChecklistHook'

const styles = {
  botoes: { textAlign: 'right' },
  botao: { marginLeft: 10 },
  card: {
    marginTop: 15,
    padding: '12px 15px 12px 15px',
    backgroundColor: '#f2fafb',
    border: '1px solid #d3edf0',
    borderLeft: `4px solid ${colors.azul}`,
    borderRadius: 3,
  },
  cardCabecalho: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
  },
  cardTitulo: { display: 'flex', alignItems: 'center', gap: 8 },
  cardTituloTexto: {
    color: colors.azul,
    fontWeight: 'bold',
    fontSize: '0.85rem',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  formulario: { fontSize: '1.1rem', fontWeight: 'bold', color: '#333', marginTop: 8 },
  rotulo: { color: colors.gray, fontSize: '0.75rem', display: 'block' },
  valor: { fontSize: '0.85rem' },
  observacao: { fontSize: '0.85rem', whiteSpace: 'pre-wrap', marginTop: 5 },
  progresso: { marginTop: 10 },
  progressoTexto: { color: colors.gray, fontSize: '0.8rem', marginBottom: 4 },
  resultado: {
    marginTop: 15,
    padding: '12px 15px',
    backgroundColor: '#f7fbf5',
    border: '1px solid #d9ead3',
    borderLeft: `4px solid ${colors.verde_success}`,
    borderRadius: 3,
  },
  percentual: { fontSize: '2rem', fontWeight: 'bold', color: colors.verde_success, lineHeight: 1 },
  percentualRotulo: { color: colors.gray, fontSize: '0.75rem' },
  badges: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  formularioContainer: { marginTop: 25 },
  rodape: { textAlign: 'right', marginTop: 25 },
}

function Info({ rotulo, children }) {
  return (
    <div className="p-md-3 p-col-6">
      <span style={styles.rotulo}>{rotulo}</span>
      <span style={styles.valor}>{children || '-'}</span>
    </div>
  )
}

function PreencherChecklist({ selectedTp, dispatch, history }) {
  const { state, localDispatch } = usePreencherChecklist()
  const tpInicial = useRef(selectedTp)
  const { preenchimento, respostas, modo } = state
  const resultadoMode = modo === MODOS.RESULTADO

  useEffect(() => {
    if (!selectedTp || selectedTp !== tpInicial.current) {
      history.replace(ROTAS_CHECKLIST.PREENCHIMENTO)
      return
    }
    localDispatch.onInit(selectedTp, history.location.state)
  }, [selectedTp])

  useEffect(() => {
    switch (state.loadingMessage) {
      case LOAD_MESSAGE.NONE:
        dispatch(hideLoadingBox())
        break
      case LOAD_MESSAGE.GET:
        dispatch(showLoadingBox(state.loadingMessage, 'Aguarde...'))
        localDispatch.getPreenchimento(selectedTp, state.origem, state.modo)
        break
      case LOAD_MESSAGE.SAVE:
        dispatch(showLoadingBox(state.loadingMessage, 'Aguarde...'))
        localDispatch.save(selectedTp, state.preenchimento, state.respostas)
        break
      case LOAD_MESSAGE.FINALIZAR:
        dispatch(showLoadingBox(state.loadingMessage, 'Aguarde...'))
        localDispatch.finalizar(selectedTp, state.preenchimento, state.respostas)
        break
      default:
        break
    }
  }, [state.loadingMessage])

  useEffect(() => {
    if (state.errorMessage) {
      dispatch(showMessageBox(null, state.errorMessage))
      if (state.focoPendenteId) {
        const alvo = document.querySelector(`[data-resposta-id="${state.focoPendenteId}"]`)
        if (alvo) alvo.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      localDispatch.clearMessages()
    }
  }, [state.errorMessage])

  useEffect(() => {
    if (state.successMessage) {
      dispatch(showMessageBox(state.successMessage, null))
      localDispatch.clearMessages()
    }
  }, [state.successMessage])

  useEffect(() => {
    if (state.voltar) history.push(ROTAS_CHECKLIST.PREENCHIMENTO)
  }, [state.voltar])

  if (!preenchimento) return <PageHeader>10.2.1 - Preencher Checklist</PageHeader>

  const status = parsePreenchimentoStatus(preenchimento.status)
  const progresso = contarRespondidos(preenchimento.estrutura, respostas)
  const percentualProgresso = progresso.total
    ? Math.round((progresso.respondidos / progresso.total) * 100)
    : 0
  // No modo resultado tudo vem gravado pela API (resultado e classificações), igual ao report impresso
  const resultado = resultadoMode ? preenchimento.resultado : null

  const botoes = (
    <div className="p-col-9" style={styles.botoes}>
      {resultadoMode ? (
        <Btn
          style={styles.botao}
          onClick={() =>
            openReportInNewTab(urlReportPreenchimentoResultado(selectedTp, preenchimento.id))
          }
        >
          <FaPrint style={{ marginRight: 6 }} />
          Imprimir Resultado
        </Btn>
      ) : (
        <Btn
          style={styles.botao}
          backgroundColor={colors.verde_success}
          onClick={localDispatch.onBtnFinalizarClicked}
        >
          Finalizar
        </Btn>
      )}
    </div>
  )

  return (
    <div>
      <PageHeader>
        {resultadoMode ? '10.2.2 - Resultado do Checklist' : '10.2.1 - Preencher Checklist'}
      </PageHeader>
      <div style={{ marginTop: 20 }}>
        <CustomPanel
          btnLabel="Salvar"
          showBtnNovo={!resultadoMode}
          onNovo={localDispatch.onBtnSalvarClicked}
          onVoltar={localDispatch.onBtnVoltarClicked}
          childrenButtons={botoes}
        >
          <div style={styles.card}>
            <div style={styles.cardCabecalho}>
              <div style={styles.cardTitulo}>
                <FaRegIdCard size={18} color={colors.azul} />
                <span style={styles.cardTituloTexto}>Identificação do preenchimento</span>
              </div>
              <Badge color={status.color}>{status.label}</Badge>
            </div>
            <div style={styles.formulario}>
              {preenchimento.checklist_code} - {preenchimento.title}
            </div>
            <div className="p-grid" style={{ marginTop: 5 }}>
              <Info rotulo="Empresa">{preenchimento.company_name}</Info>
              <Info rotulo="Responsável">{preenchimento.responsavel}</Info>
              <Info rotulo="Criado em">{formatarDataHora(preenchimento.created_at)}</Info>
              <Info rotulo="Deadline">{formatarDataHora(preenchimento.deadline)}</Info>
              {preenchimento.observacao ? (
                <div className="p-col-12">
                  <span style={styles.rotulo}>Observação</span>
                  <div style={styles.observacao}>{preenchimento.observacao}</div>
                </div>
              ) : null}
            </div>
            {!resultadoMode ? (
              <div style={styles.progresso}>
                <div style={styles.progressoTexto}>
                  Progresso: {progresso.respondidos} de {progresso.total} respondidos (
                  {percentualProgresso}%)
                </div>
                <ProgressBar value={percentualProgresso} showValue={false} />
              </div>
            ) : null}
          </div>

          {resultado ? (
            <div style={styles.resultado}>
              <div style={styles.cardCabecalho}>
                <div style={styles.cardTitulo}>
                  <FaChartPie size={18} color={colors.verde_success} />
                  <span style={{ ...styles.cardTituloTexto, color: colors.verde_success }}>
                    Resultado final
                  </span>
                </div>
                <span style={styles.percentualRotulo}>
                  Finalizado em {formatarDataHora(preenchimento.finalizado_em)}
                </span>
              </div>
              <div className="p-grid" style={{ marginTop: 5, alignItems: 'center' }}>
                <div className="p-md-3 p-col-12">
                  <div style={styles.percentual}>{formatarPercentual(resultado.percentual)}</div>
                  <span style={styles.percentualRotulo}>
                    Índice de sucesso (sucessos ÷ sucessos + falhas)
                  </span>
                </div>
                <div className="p-md-9 p-col-12" style={styles.badges}>
                  <Badge color={ResultadoResposta.success.color}>
                    Sucesso: {resultado.success}
                  </Badge>
                  <Badge color={ResultadoResposta.fail.color}>Falha: {resultado.fail}</Badge>
                  <Badge color={ResultadoResposta.neutral.color}>Neutro: {resultado.neutral}</Badge>
                  <Badge color={colors.azul}>Informativos: {resultado.informational}</Badge>
                  <span style={styles.percentualRotulo}>
                    {resultado.scored} itens pontuados de {resultado.total}
                  </span>
                </div>
              </div>
            </div>
          ) : null}

          <div style={styles.formularioContainer}>
            <ChecklistForm
              checklist={preenchimento.estrutura}
              respostas={respostas}
              onChange={localDispatch.onRespostasChange}
              somenteLeitura={resultadoMode}
              pendentes={state.pendentes}
              classificacoes={resultadoMode ? preenchimento.classificacoes : null}
              mostrarClassificacao={resultadoMode}
              mostrarProgresso={!resultadoMode}
            />
          </div>

          {!resultadoMode ? (
            <div style={styles.rodape}>
              <Btn onClick={localDispatch.onBtnSalvarClicked}>Salvar</Btn>
              <Btn
                style={styles.botao}
                backgroundColor={colors.verde_success}
                onClick={localDispatch.onBtnFinalizarClicked}
              >
                Finalizar
              </Btn>
            </div>
          ) : null}
        </CustomPanel>
      </div>

      <ConfirmDialog
        isVisible={state.confirmFinalizar}
        title="Finalizar preenchimento"
        content={
          <span>Após Finalizar não será mais possível alterar o preenchimento do formulário</span>
        }
        okLabel="FINALIZAR"
        onCancelar={() => localDispatch.onConfirmFinalizar(false)}
        onOk={() => localDispatch.onConfirmFinalizar(true)}
      />
      <ConfirmDialog
        isVisible={state.confirmVoltar}
        title="Atenção"
        content={<span>Existem respostas não salvas. Deseja sair sem salvar?</span>}
        okLabel="SAIR"
        onCancelar={() => localDispatch.onConfirmVoltar(false)}
        onOk={() => localDispatch.onConfirmVoltar(true)}
      />
    </div>
  )
}

const mapStateToProps = ({ system: { selectedTp } }) => ({ selectedTp })
export default connect(mapStateToProps)(withRouter(PreencherChecklist))
